import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import SidebarLayout from "../../components/SidebarLayout";
import Modal from "../../components/Modal";
import { BACKEND_URL } from "../../config";

/* ------------------ Types ------------------ */
type Gender = "Male" | "Female" | "Other" | "";

type EmployeeType = "DOCTOR" | "FRONT_DESK" | "PHARMACY" | "ADMIN" | "LAB_TECHNICIAN" | "IT_STAFF" | "BILLING";

interface ContactForm {
  name: string;
  gender: Gender;
  phone: string;
  dob?: string;
  address?: string;
  email?: string;
  type: string;
}

interface EmployeeForm {
  id?: number;
  name: string;
  type: EmployeeType;
  tamil_name?: string;
  specializations?: string;
  tamil_specialization?: string;
  registration_number?: string;
  description?: string;
  contact_id?: number;
  contact?: ContactForm;
  authorizations?: { role_id: number }[];
}

interface RolesForm {
  role_id: number;
}

interface AvailableRole {
  id: number;
  name: string;
}

const roleDisplayNamesById: Record<number, string> = {
  1: "Doctor",
  2: "Billing",
  3: "Front Desk",
  4: "Pharmacy",
  5: "Admin",
  6: "IT Staff",
  7: "Lab Technician",
};

const unavailableRoleIds = new Set([6, 7]);
const isRoleAvailable = (roleId: number) => !unavailableRoleIds.has(roleId);

/* ------------------ Component ------------------ */
const AddEmployee: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const employeeData: EmployeeForm | undefined = (location.state as any)?.employee;
  const isUpdate = Boolean(employeeData?.id);
  const isView = location.state?.view === true;

  const [loading, setLoading] = useState(false);

  const [modal, setModal] = useState({
    open: false,
    title: "",
    message: "",
    redirectTo: undefined as string | undefined,
  });

  /* ------------------ Forms ------------------ */
  const [contact, setContact] = useState<ContactForm>({
    name: "",
    gender: "",
    phone: "",
    dob: "",
    address: "",
    email: "",
    type: "EMPLOYEE",
  });

  const [roles, setRoles] = useState<RolesForm[]>([]);
  const [availableRoles, setAvailableRoles] = useState<AvailableRole[]>([]);

  const [employee, setEmployee] = useState<EmployeeForm>({
    name: "",
    type: "DOCTOR",
    tamil_name: "",
    specializations: "",
    tamil_specialization: "",
    registration_number: "",
    description: "",
    contact: {
      name: "",
      gender: "",
      phone: "",
      dob: "",
      email: "",
      address: "",
      type: "EMPLOYEE",
    },
  });

  const employeeTypeOptions: Array<{ value: EmployeeType; label: string }> = [
    { value: "DOCTOR", label: "Doctor" },
    { value: "FRONT_DESK", label: "Front Desk" },
    { value: "PHARMACY", label: "Pharmacy" },
    { value: "ADMIN", label: "Admin" },
    { value: "LAB_TECHNICIAN", label: "Lab Technician" },
    { value: "IT_STAFF", label: "IT Staff" },
    { value: "BILLING", label: "Billing" },
  ];

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/roles`, {
      credentials: "include",
    })
      .then((res) => {
        if (!res.ok) throw new Error("Roles fetch failed");
        return res.json();
      })
      .then((data) => {
        setAvailableRoles(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setAvailableRoles([]);
        setModal({
          open: true,
          title: "Error",
          message: "Failed to load available roles",
          redirectTo: undefined,
        });
      });
  }, []);

  useEffect(() => {
    if (employeeData) {
      setEmployee(employeeData);

      // preload name into contact as well
      setContact((prev) => ({
        ...prev,
        name: employeeData.contact?.name || "",
        gender: employeeData.contact?.gender || "",
        phone: employeeData.contact?.phone || "",
        dob: employeeData.contact?.dob || "",
        email: employeeData.contact?.email || "",
        address: employeeData.contact?.address || "",
        type: employeeData.contact?.type || "",
      }));

      //preload roles
      setRoles(
        employeeData.authorizations
          ?.filter((auth) => isRoleAvailable(auth.role_id))
          .map((auth) => ({ role_id: auth.role_id })) || []
      );
    }
  }, [employeeData]);

  useEffect(() => {
    if (!isUpdate || !employeeData?.id || employeeData.authorizations?.length) return;

    fetch(`${BACKEND_URL}/api/authorizations/${employeeData.id}`, {
      credentials: "include",
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((authorizations) => {
        if (Array.isArray(authorizations)) {
          setRoles(
            authorizations
              .filter((auth) => isRoleAvailable(auth.role_id))
              .map((auth) => ({ role_id: auth.role_id }))
          );
        }
      })
      .catch(() => setRoles([]));
  }, [isUpdate, employeeData?.id, employeeData?.authorizations?.length]);

  /* ------------------ Handlers ------------------ */
  const handleContactChange = (k: keyof ContactForm, v: any) =>
    setContact((p) => ({ ...p, [k]: v }));

  const handleEmployeeChange = (k: keyof EmployeeForm, v: any) =>
    setEmployee((p) => ({ ...p, [k]: v }));

  const toggleRole = (roleId: number) => {
    if (!isRoleAvailable(roleId)) return;

    setRoles((prev) => {
      const exists = prev.some((r) => r.role_id === roleId);

      if (exists) {
        // remove role
        return prev.filter((r) => r.role_id !== roleId);
      }

      // add role
      return [...prev, { role_id: roleId }];
    });
  };


  /* ------------------ Submit ------------------ */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isView) return;

    const selectedAvailableRoles = roles.filter((role) => isRoleAvailable(role.role_id));

    if (selectedAvailableRoles.length === 0) {
      setModal({
        open: true,
        title: "Validation Error",
        message: "Select at least one role before creating access",
        redirectTo: undefined,
      });
      return;
    }

    setLoading(true);
    try {
      // 1️⃣ Create Contact
      let contactId = employee.contact_id;
      let employeeId = employee.id;

      /* ---------------- CONTACT ---------------- */

      if (isUpdate && contactId) {
        // ✅ UPDATE existing contact
        const contactRes = await fetch(
          `${BACKEND_URL}/api/contacts/${contactId}`,
          {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(contact),
          }
        );

        if (!contactRes.ok) throw new Error("Contact update failed");
      } else {
        // ✅ CREATE new contact
        const contactRes = await fetch(`${BACKEND_URL}/api/contacts/`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(contact),
        });

        if (!contactRes.ok) throw new Error("Contact create failed");
        const createdContact = await contactRes.json();
        contactId = createdContact.contact?.id || createdContact.id;
        if (!contactId) throw new Error("Contact create failed: missing contact id");
      }

      /* ---------------- EMPLOYEE ---------------- */
      // 2️⃣ Create / Update Employee
      const url = isUpdate
        ? `${BACKEND_URL}/api/employees/${employeeData?.id}`
        : `${BACKEND_URL}/api/employees/`;

      const method = isUpdate ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...employee,
          contact_id: contactId,
        }),
      });

      if (!res.ok) throw new Error();

      employeeId = (await res.json()).id;

      /* ---------------- ROLES ---------------- */
      // 2️⃣ Create / Update Roles
      const roleUrl = isUpdate
        ? `${BACKEND_URL}/api/authorizations/${employeeId}`
        : `${BACKEND_URL}/api/authorizations/`;

      const rolesRes = await fetch(roleUrl, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          authorizations: selectedAvailableRoles.map((r) => ({
            role_id: r.role_id,
            employee_id: employeeId,
          })),
        }),
      });

      if (!rolesRes.ok) {
        const errorText = await rolesRes.text();
        throw new Error(errorText || "Authorization save failed");
      }

      setModal({
        open: true,
        title: "Success",
        message: `Employee ${isUpdate ? "updated" : "created"} successfully`,
        redirectTo: "/employees",
      });
    } catch (err) {
      console.error(err);
      setModal({
        open: true,
        title: "Error",
        message: "Failed to save employee",
        redirectTo: "/employees",
      });
    } finally {
      setLoading(false);
    }
  };

  /* ------------------ UI ------------------ */
  return (
    <SidebarLayout
      title={isView ? "View Employee" : isUpdate ? "Edit Employee" : "Add Employee"}
    >
      <Modal
        open={modal.open}
        title={modal.title}
        message={modal.message}
        onClose={() => {
          const r = modal.redirectTo;
          setModal({ ...modal, open: false });
          if (r) navigate(r);
        }}
      />

      <div className="bg-surface-light p-6 rounded border max-w-3xl">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">

          {/* CONTACT DETAILS */}
          <h3 className="font-semibold text-lg">Contact Details</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              placeholder="Name"
              disabled={isView}
              value={contact.name}
              onChange={(e) => {
                const v = e.target.value;
                handleContactChange("name", v);
                handleEmployeeChange("name", v);
              }}
              className="p-3 border rounded"
              required
            />

            <select
              disabled={isView}
              value={contact.gender}
              onChange={(e) => handleContactChange("gender", e.target.value)}
              className="p-3 border rounded"
            >
              <option value="">Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              placeholder="Phone"
              disabled={isView}
              value={contact.phone}
              onChange={(e) => handleContactChange("phone", e.target.value)}
              className="p-3 border rounded"
              required
            />

            <input
              type="date"
              disabled={isView}
              value={contact.dob}
              onChange={(e) => handleContactChange("dob", e.target.value)}
              className="p-3 border rounded"
            />
          </div>

          <input
            type="email"
            required
            placeholder="Email"
            disabled={isView}
            value={contact.email}
            onChange={(e) => handleContactChange("email", e.target.value)}
            className="p-3 border rounded"
          />


          <input
            placeholder="Address"
            disabled={isView}
            value={contact.address}
            onChange={(e) => handleContactChange("address", e.target.value)}
            className="p-3 border rounded"
          />


          {/* EMPLOYEE DETAILS */}
          <h3 className="font-semibold text-lg pt-4">Employee Details</h3>

          <select
            disabled={isView}
            value={employee.type}
            onChange={(e) => handleEmployeeChange("type", e.target.value)}
            className="p-3 border rounded"
          >
            {employeeTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>

          {employee.type === "DOCTOR" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                placeholder="Tamil Doctor Name"
                disabled={isView}
                value={employee.tamil_name}
                onChange={(e) =>
                  handleEmployeeChange("tamil_name", e.target.value)
                }
                className="p-3 border rounded"
              />
              <input
                placeholder="Registration Number"
                disabled={isView}
                value={employee.registration_number}
                onChange={(e) =>
                  handleEmployeeChange("registration_number", e.target.value)
                }
                className="p-3 border rounded"
              />
              <input
                placeholder="Specialization"
                disabled={isView}
                value={employee.specializations}
                onChange={(e) =>
                  handleEmployeeChange("specializations", e.target.value)
                }
                className="p-3 border rounded"
              />
              <input
                placeholder="Tamil Specialization"
                disabled={isView}
                value={employee.tamil_specialization}
                onChange={(e) =>
                  handleEmployeeChange("tamil_specialization", e.target.value)
                }
                className="p-3 border rounded"
              />
            </div>
          )}

          <textarea
            placeholder="Description"
            disabled={isView}
            value={employee.description}
            onChange={(e) =>
              handleEmployeeChange("description", e.target.value)
            }
            className="p-3 border rounded"
          />

          {/* Roles DETAILS */}
          <h3 className="font-semibold text-lg"> Roles Details</h3>
          <div className="grid grid-cols-2 gap-2">
            {availableRoles.map((role) => {
              const isAvailable = isRoleAvailable(role.id);

              return (
              <label key={role.id} className={`flex items-center gap-2 ${isAvailable ? "" : "text-gray-400"}`}>
                <input
                  type="checkbox"
                  checked={roles.some((r) => r.role_id === role.id)}
                  onChange={() => toggleRole(role.id)}
                  disabled={isView || !isAvailable}
                />
                {roleDisplayNamesById[role.id] || role.name}
                {!isAvailable && <span className="text-xs">(Unavailable)</span>}
              </label>
              );
            })}
          </div>

          {/* ACTIONS */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate("/employees")}
              className="px-4 py-2 rounded bg-background-light"
            >
              Back
            </button>

            {!isView && (
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 rounded bg-primary text-white"
              >
                {loading ? "Saving..." : isUpdate ? "Update" : "Save"}
              </button>
            )}
          </div>
        </form>
      </div>
    </SidebarLayout>
  );
};

export default AddEmployee;
